import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

// Impact page CMS. Mirrors the Social Run recap contract:
//   - draft / ready / live global state
//   - per-section draft → publish workflow
//   - drag-drop reorder
//   - bulk publish with pre-flight analysis modal
//   - admin preview link
// Everything editable in EN / DA / ES with per-language warnings.

const META = {
  hero:               "Hero",
  impact_at_glance:   "Impact at a glance",
  valencia_2024:      "Valencia 2024",
  social_run:         "Social Run Copenhagen 2026",
  renace_avila:       "RENACE Ávila",
  how_impact_happens: "How impact happens",
  transparency:       "Transparency",
  final_cta:          "Final CTA",
};

const LANGS = ["en", "da", "es"];

export default function ImpactAdmin({ authed }) {
  const [sections, setSections] = useState(null);
  const [meta, setMeta] = useState(null);
  const [analysis, setAnalysis] = useState({});
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [publishModal, setPublishModal] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [rSec, rMeta, rAna] = await Promise.all([
      authed.get("/content?kind=impact_section&status="),
      authed.get("/admin/impact/meta"),
      authed.get("/admin/impact/analysis"),
    ]);
    const rows = (rSec.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
    setMeta(rMeta.data || {});
    const byId = {};
    (rAna.data.sections || []).forEach(s => { byId[s.id] = s; });
    setAnalysis(byId);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    const reord = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, data: { ...s.data, order: i + 1 } }));
    setSections(reord);
    await authed.post("/admin/impact/reorder", { order: reord.map((s, i) => ({ id: s.id, order: i + 1 })) });
  };

  const setState = async (next) => {
    const cur = meta?.state || "draft";
    if (next === cur) return;
    const prompts = {
      draft: "Revert to DRAFT? Public /impact returns to the LEGACY layout. No content deleted.",
      ready: "Mark impact page as READY? Public still sees the LEGACY layout. Share admin previews with reviewers.",
      live:  "Set impact page LIVE? /impact will render the NEW layout for every visitor.",
    };
    if (!window.confirm(prompts[next])) return;
    const r = await authed.put("/admin/impact/meta", { state: next });
    setMeta(r.data);
    const labels = { draft: "DRAFT — legacy layout restored.", ready: "READY — legacy stays public, preview enabled.", live: "LIVE — new /impact is public." };
    setMsg(labels[next]);
    setTimeout(() => setMsg(""), 3500);
  };

  const seedMissing = async () => {
    if (!window.confirm("Insert the 8 default draft sections? Skips sections that already exist.")) return;
    const r = await authed.post("/admin/impact/seed-missing");
    setMsg(r.data.inserted > 0 ? `Seeded ${r.data.inserted} draft sections.` : "Already seeded — nothing to do.");
    await load();
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const openPublishModal = async (scope) => {
    const ids = scope === "selected" ? Array.from(selected) : null;
    const r = await authed.post("/admin/impact/publish-batch", { ids, dry_run: true });
    setPublishModal({ scope, ids, dry: r.data });
  };
  const confirmPublish = async () => {
    const { scope, ids } = publishModal;
    setPublishModal({ ...publishModal, busy: true });
    const r = await authed.post("/admin/impact/publish-batch", { ids: scope === "selected" ? ids : null });
    setPublishModal({ ...publishModal, result: r.data });
    setSelected(new Set());
    await load();
  };

  if (sections === null) return <div className="text-ink/50 text-sm">Loading…</div>;

  const publishedCount = sections.filter(s => s.status === "published" && s.data?.visible !== false).length;
  const draftPendingCount = sections.filter(s => !!s.data_draft).length;
  const state = meta?.state || "draft";
  const stateColor = { draft: "bg-line/30 text-ink/70", ready: "bg-amber-100 text-amber-800", live: "bg-emerald-100 text-emerald-800" }[state];
  const stateCopy = {
    draft: "Public visitors see the LEGACY /impact. Curate every section, then advance to READY.",
    ready: "Public still sees LEGACY. Use the admin preview to review before going LIVE.",
    live:  "Public visitors see the NEW /impact. Reverting to DRAFT restores the legacy layout without deleting content.",
  }[state];

  return (
    <div className="space-y-6" data-testid="impact-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="label-mono text-terracotta text-[10px]">IMPACT · /impact</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Impact page</h2>
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="impact-state-badge"
                    className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${stateColor}`}>{state.toUpperCase()}</span>
              <a href="/impact?preview=1" target="_blank" rel="noreferrer"
                 data-testid="impact-preview-link"
                 className="text-[11px] underline text-ink/70 hover:text-ink">Open admin preview ↗</a>
            </div>
            <p className="mt-3 text-xs text-ink/60 max-w-xl">{stateCopy}</p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[260px]">
            <div className="text-[11px] text-ink/60">
              <span className="text-emerald-700 font-medium">{publishedCount}</span> published
              {" · "}
              <span className="text-amber-700 font-medium">{draftPendingCount}</span> with pending drafts
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setState("draft")} disabled={state === "draft"} data-testid="impact-state-draft"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${state === "draft" ? "border-line text-ink/40 bg-line/10 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
                {state === "live" ? "Revert to legacy" : "Set draft"}
              </button>
              <button onClick={() => setState("ready")} disabled={state === "ready"} data-testid="impact-state-ready"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "ready" ? "bg-amber-100 text-amber-800 cursor-default" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
                Mark ready
              </button>
              <button onClick={() => setState("live")} disabled={state === "live"} data-testid="impact-state-live"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "live" ? "bg-emerald-600 text-white cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                Set impact live
              </button>
            </div>
            {sections.length === 0 && (
              <button onClick={seedMissing} data-testid="impact-seed-missing"
                      className="mt-1 px-3 py-2 text-[11px] uppercase tracking-widest border border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
                Seed 8 default drafts
              </button>
            )}
            {msg && <div className="text-[11px] text-ink/60">{msg}</div>}
          </div>
        </div>
      </section>

      <section className="border border-line rounded-xl p-4 bg-white flex flex-wrap items-center gap-3">
        <div className="text-[11px] text-ink/60 flex-1 min-w-[220px]">
          <span className="font-medium text-ink">Bulk publish</span> · publishes section content only — never touches page state.
          {selected.size > 0 && <span className="ml-2 text-terracotta">{selected.size} selected</span>}
        </div>
        <button onClick={() => openPublishModal("selected")} disabled={selected.size === 0}
                data-testid="impact-publish-selected"
                className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${selected.size === 0 ? "border-line text-ink/30 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
          Publish selected
        </button>
        <button onClick={() => openPublishModal("all")}
                data-testid="impact-publish-all"
                className="px-3 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">
          Publish all sections
        </button>
      </section>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="impact-sections-list">
            {sections.map((s, idx) => (
              <SortableRow key={s.id} s={s} idx={idx}
                           onEdit={() => setEditing(s)}
                           analysis={analysis[s.id]}
                           checked={selected.has(s.id)}
                           onToggleCheck={() => toggleSelected(s.id)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editing && (
        <SectionEditor s={editing} authed={authed}
                       onClose={() => { setEditing(null); load(); }} />
      )}

      {publishModal && (
        <PublishBatchModal modal={publishModal} onConfirm={confirmPublish} onClose={() => setPublishModal(null)} />
      )}
    </div>
  );
}

function SortableRow({ s, idx, onEdit, analysis, checked, onToggleCheck }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published" || d.visible === false;
  const hasDraft = !!s.data_draft;
  const name = META[d.slug] || d.slug;
  const a = analysis || {};
  const missingLangs = a.missing_translation || [];
  return (
    <div ref={setNodeRef} style={style} data-testid={`impact-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <input type="checkbox" checked={!!checked} onChange={onToggleCheck}
             data-testid={`impact-check-${d.slug}`}
             className="w-4 h-4 cursor-pointer accent-terracotta" onClick={(e) => e.stopPropagation()} />
      <button {...attributes} {...listeners} className="cursor-grab text-ink/30 hover:text-ink text-xl px-2">≡</button>
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{idx + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" />
               : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base truncate">{name}</div>
        <div className="text-[11px] text-ink/50 truncate">{d.template} · {(d.headline?.en || d.headline?.da || "").slice(0, 60)}</div>
        {(a.missing_image || a.missing_headline || a.missing_link || missingLangs.length > 0) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {a.missing_headline && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">NO HEADLINE</span>}
            {a.missing_image && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">MISSING IMAGE</span>}
            {a.missing_link && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">MISSING LINK</span>}
            {missingLangs.map(l => (
              <span key={l} className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">MISSING {l.toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && <span className="text-[9px] label-mono tracking-widest px-2 py-1 rounded bg-amber-100 text-amber-800">DRAFT</span>}
        <span className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
          {hidden ? (s.status === "published" ? "HIDDEN" : "DRAFT") : "PUBLISHED"}
        </span>
        <button onClick={onEdit} data-testid={`impact-edit-${d.slug}`}
                className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
      </div>
    </div>
  );
}

// ─── Section editor ───────────────────────────────────────────────────
function SectionEditor({ s, authed, onClose }) {
  const [data, setData] = useState(s.data_draft || s.data || {});
  const [lang, setLang] = useState("en");
  const [pickerFor, setPickerFor] = useState(null); // { kind: 'section'|'partner'|'cause_logo', idx?: n, field?: 'logo_url' }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const patch = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const patchLang = (k, v) => setData(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [lang]: v } }));
  const patchArrItem = (arrKey, i, obj) => {
    const arr = [...(data[arrKey] || [])];
    arr[i] = { ...(arr[i] || {}), ...obj };
    patch(arrKey, arr);
  };
  const patchArrItemLang = (arrKey, i, k, v) => {
    const arr = [...(data[arrKey] || [])];
    const cur = arr[i] || {};
    arr[i] = { ...cur, [k]: { ...(cur[k] || {}), [lang]: v } };
    patch(arrKey, arr);
  };
  const removeArrItem = (arrKey, i) => {
    const arr = [...(data[arrKey] || [])];
    arr.splice(i, 1);
    patch(arrKey, arr);
  };
  const addArrItem = (arrKey, tpl) => {
    const arr = [...(data[arrKey] || []), tpl];
    patch(arrKey, arr);
  };

  const saveDraft = async () => {
    setBusy(true);
    await authed.put(`/content/${s.id}/draft`, { data });
    setMsg("Draft saved.");
    setBusy(false);
    setTimeout(() => setMsg(""), 1400);
  };
  const publish = async () => {
    const missingEn = ["headline", "body"].some(k => (data[k] || {}).en === "" || !(data[k] || {}).en);
    if (missingEn && !window.confirm("This section is missing some English content. Publish anyway?")) return;
    if (!window.confirm("Publish this section? It will replace the current published version (still not visible publicly until state=LIVE).")) return;
    setBusy(true);
    await authed.post(`/content/${s.id}/publish`, { data });
    setBusy(false);
    onClose();
  };
  const discardDraft = async () => {
    if (!window.confirm("Discard draft changes?")) return;
    await authed.post(`/content/${s.id}/discard-draft`);
    onClose();
  };
  const toggleVisible = () => patch("visible", data.visible === false ? true : false);

  const t = data.template;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" role="dialog">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" data-testid={`impact-editor-${data.slug}`}>
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="label-mono text-terracotta text-[10px]">EDIT SECTION</div>
            <div className="font-editorial text-xl">{META[data.slug] || data.slug}</div>
            <div className="text-[10px] text-ink/40 mt-0.5">Template: {t}</div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[10px] uppercase tracking-widest text-ink/60 flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={data.visible !== false} onChange={toggleVisible} data-testid="impact-visible-toggle" />
              Show on page
            </label>
            <button onClick={onClose} className="text-2xl text-ink/50 hover:text-ink px-2">×</button>
          </div>
        </div>

        <div className="px-6 pt-5 border-b border-line pb-4 flex items-center gap-2">
          <span className="label-mono text-ink/60 text-[10px] mr-1">LANGUAGE</span>
          {LANGS.map(l => (
            <button key={l} onClick={() => setLang(l)} data-testid={`impact-lang-${l}`}
                    className={`text-[10px] label-mono px-3 py-1 rounded ${lang === l ? "bg-ink text-white" : "bg-line/30 text-ink/60"}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5">
          <Field label={`Eyebrow (${lang})`}>
            <input value={(data.eyebrow || {})[lang] || ""} onChange={e => patchLang("eyebrow", e.target.value)}
                   className="border border-line px-3 py-2 w-full label-mono tracking-widest text-sm" />
          </Field>
          <Field label={`Headline (${lang})`}>
            <textarea value={(data.headline || {})[lang] || ""} onChange={e => patchLang("headline", e.target.value)}
                      rows={2} className="border border-line px-3 py-2 w-full font-editorial text-2xl leading-tight" />
          </Field>
          <Field label={`Body (${lang})`}>
            <textarea value={(data.body || {})[lang] || ""} onChange={e => patchLang("body", e.target.value)}
                      rows={5} className="border border-line px-3 py-2 w-full text-sm leading-relaxed" />
          </Field>

          {(t === "hero" || t === "partner_cards" || t === "social_run_feature" || t === "editorial_text" || t === "final_cta") && (
            <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-line/50">
              <div>
                <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">Primary CTA</div>
                <input value={(data.cta_label || {})[lang] || ""} onChange={e => patchLang("cta_label", e.target.value)}
                       placeholder={`label (${lang})`} className="w-full border border-line px-3 py-2 text-sm" />
                <input value={data.cta_url || ""} onChange={e => patch("cta_url", e.target.value)}
                       placeholder="URL (external or internal e.g. /join)"
                       className="mt-2 w-full border border-line px-3 py-2 text-sm font-mono" />
              </div>
              {t === "final_cta" && (
                <div>
                  <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">Secondary CTA</div>
                  <input value={(data.cta2_label || {})[lang] || ""} onChange={e => patchLang("cta2_label", e.target.value)}
                         placeholder={`label (${lang})`} className="w-full border border-line px-3 py-2 text-sm" />
                  <input value={data.cta2_url || ""} onChange={e => patch("cta2_url", e.target.value)}
                         placeholder="URL"
                         className="mt-2 w-full border border-line px-3 py-2 text-sm font-mono" />
                </div>
              )}
            </div>
          )}

          {t === "hero" && (
            <div className="pt-3 border-t border-line/50">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={data.show_headline !== false}
                       onChange={e => patch("show_headline", e.target.checked)}
                       data-testid="impact-hero-show-headline"
                       className="mt-1 accent-terracotta" />
                <div>
                  <div className="text-sm font-medium">Show hero headline over the photograph</div>
                  <div className="text-[11px] text-ink/50 mt-0.5">Turn off to let the photograph carry the opening.</div>
                </div>
              </label>
            </div>
          )}

          {/* Images */}
          {(t === "hero" || t === "partner_cards" || t === "social_run_feature") && (
            <ImageSlot data={data} onPick={() => setPickerFor({ kind: "section", idx: 0 })}
                       onRemove={() => patch("images", [{}])}
                       onFocal={(k, v) => {
                         const im = { ...(data.images?.[0] || {}), [k]: v };
                         patch("images", [im]);
                       }}
                       onAlt={v => {
                         const im = { ...(data.images?.[0] || {}) };
                         im.alt = { ...(im.alt || {}), [lang]: v };
                         patch("images", [im]);
                       }}
                       lang={lang} />
          )}

          {/* Stats grid (Impact at a glance) */}
          {t === "stats_grid" && (
            <div className="pt-3 border-t border-line/50 space-y-3" data-testid="impact-stats-editor">
              <div className="flex items-center justify-between">
                <div className="label-mono text-[11px] tracking-widest text-ink/60">Stats ({lang})</div>
                <button onClick={() => addArrItem("stats", { value: "", label: { en: "", da: "", es: "" }, note: { en: "", da: "", es: "" } })}
                        className="text-[10px] uppercase tracking-widest border border-ink px-2 py-1 hover:bg-ink hover:text-white">+ Add</button>
              </div>
              {(data.stats || []).map((st, i) => (
                <div key={i} className="border border-line rounded p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input value={st.value || ""} onChange={e => patchArrItem("stats", i, { value: e.target.value })}
                           placeholder="200,000+ DKK" className="border border-line px-3 py-2 w-44 font-editorial" />
                    <input value={(st.label || {})[lang] || ""}
                           onChange={e => patchArrItemLang("stats", i, "label", e.target.value)}
                           placeholder="label" className="border border-line px-3 py-2 flex-1" />
                    <button onClick={() => removeArrItem("stats", i)} className="text-rose-600 text-lg px-2">×</button>
                  </div>
                  <input value={(st.note || {})[lang] || ""}
                         onChange={e => patchArrItemLang("stats", i, "note", e.target.value)}
                         placeholder={`optional note (${lang})`} className="border border-line px-3 py-2 w-full text-[13px] italic" />
                </div>
              ))}
            </div>
          )}

          {/* Social run stats */}
          {t === "social_run_feature" && (
            <div className="pt-3 border-t border-line/50 space-y-3">
              <div className="label-mono text-[11px] tracking-widest text-ink/60">Stats ({lang} label)</div>
              {(data.stats || []).map((st, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={st.value || ""} onChange={e => patchArrItem("stats", i, { value: e.target.value })}
                         className="border border-line px-3 py-2 w-40 font-editorial" />
                  <input value={(st.label || {})[lang] || ""}
                         onChange={e => patchArrItemLang("stats", i, "label", e.target.value)}
                         className="border border-line px-3 py-2 flex-1" />
                </div>
              ))}
            </div>
          )}

          {/* Partner cards */}
          {t === "partner_cards" && (
            <div className="pt-3 border-t border-line/50 space-y-4" data-testid="impact-partners-editor">
              <div className="flex items-center justify-between">
                <div className="label-mono text-[11px] tracking-widest text-ink/60">Organisations ({lang})</div>
                <button onClick={() => addArrItem("partners", {
                          name: "", logo_url: "", url: "",
                          description: { en: "", da: "", es: "" },
                          cta_label: { en: "Visit their website", da: "Besøg deres hjemmeside", es: "Visita su web" },
                          name_link: true, logo_link: true,
                        })}
                        className="text-[10px] uppercase tracking-widest border border-ink px-2 py-1 hover:bg-ink hover:text-white">+ Add organisation</button>
              </div>
              {(data.partners || []).map((p, i) => (
                <div key={i} className="border border-line rounded p-3 space-y-2 bg-white">
                  <div className="flex items-center justify-between">
                    <input value={p.name || ""} onChange={e => patchArrItem("partners", i, { name: e.target.value })}
                           placeholder="Organisation name" className="border border-line px-3 py-2 flex-1 mr-2 font-editorial text-lg" />
                    <button onClick={() => removeArrItem("partners", i)} className="text-rose-600 text-lg px-2">×</button>
                  </div>
                  <textarea value={(p.description || {})[lang] || ""}
                            onChange={e => patchArrItemLang("partners", i, "description", e.target.value)}
                            placeholder={`Short description (${lang})`}
                            rows={2} className="border border-line px-3 py-2 w-full text-sm" />
                  <input value={p.url || ""} onChange={e => patchArrItem("partners", i, { url: e.target.value })}
                         placeholder="Website URL"
                         className="border border-line px-3 py-2 w-full text-sm font-mono" />
                  <input value={(p.cta_label || {})[lang] || ""}
                         onChange={e => patchArrItemLang("partners", i, "cta_label", e.target.value)}
                         placeholder={`Button label (${lang})`} className="border border-line px-3 py-2 w-full text-sm" />
                  <div className="flex items-center gap-4 text-[10px] text-ink/70">
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={p.name_link !== false}
                                                                       onChange={e => patchArrItem("partners", i, { name_link: e.target.checked })} /> Name clickable</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={p.logo_link !== false}
                                                                       onChange={e => patchArrItem("partners", i, { logo_link: e.target.checked })} /> Logo clickable</label>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.logo_url ? (
                      <>
                        <img src={resolveImageUrl(p.logo_url)} alt={p.name} className="max-h-10 border border-line p-1 bg-white" />
                        <button onClick={() => patchArrItem("partners", i, { logo_url: "" })}
                                className="text-[10px] uppercase tracking-widest text-ink/60 hover:text-rose-600">Remove logo</button>
                      </>
                    ) : (
                      <button onClick={() => setPickerFor({ kind: "partner_logo", idx: i })}
                              className="text-[10px] uppercase tracking-widest border border-line px-2 py-1 hover:border-ink">+ Add logo</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cause card (RENACE Ávila) */}
          {t === "cause_card" && (
            <CauseEditor data={data} patch={patch} patchLang={patchLang} lang={lang}
                         onPickLogo={() => setPickerFor({ kind: "cause_logo" })} />
          )}

          {/* Steps */}
          {t === "steps_three" && (
            <div className="pt-3 border-t border-line/50 space-y-3">
              <div className="label-mono text-[11px] tracking-widest text-ink/60">Steps ({lang})</div>
              {(data.steps || []).map((st, i) => (
                <div key={i} className="border border-line rounded p-3 space-y-2">
                  <input value={(st.name || {})[lang] || ""}
                         onChange={e => patchArrItemLang("steps", i, "name", e.target.value)}
                         placeholder="Step name" className="border border-line px-3 py-2 w-full font-editorial text-lg" />
                  <textarea value={(st.text || {})[lang] || ""}
                            onChange={e => patchArrItemLang("steps", i, "text", e.target.value)}
                            rows={2} placeholder="One short sentence"
                            className="border border-line px-3 py-2 w-full text-sm" />
                </div>
              ))}
            </div>
          )}

          {msg && <div className="text-[11px] text-emerald-700">{msg}</div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-line px-6 py-4 flex flex-wrap gap-2 justify-end">
          {s.data_draft && (
            <button onClick={discardDraft} disabled={busy}
                    className="px-4 py-2 text-[11px] uppercase tracking-widest border border-rose-300 text-rose-600 hover:bg-rose-50">
              Discard draft
            </button>
          )}
          <button onClick={saveDraft} disabled={busy}
                  data-testid="impact-save-draft"
                  className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">
            Save draft
          </button>
          <button onClick={publish} disabled={busy}
                  data-testid="impact-publish"
                  className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white hover:bg-terracotta-dark">
            {busy ? "…" : "Publish"}
          </button>
        </div>
      </div>

      {pickerFor && (
        <ImagePicker
          onPick={(asset) => {
            if (pickerFor.kind === "section") {
              const im = { ...(data.images?.[0] || {}) };
              im.url = asset.url;
              if (!im.alt) im.alt = { en: asset.alt_text || "", da: asset.alt_text || "", es: asset.alt_text || "" };
              patch("images", [im]);
            } else if (pickerFor.kind === "partner_logo") {
              patchArrItem("partners", pickerFor.idx, { logo_url: asset.url });
            } else if (pickerFor.kind === "cause_logo") {
              patch("cause", { ...(data.cause || {}), logo_url: asset.url });
            }
            setPickerFor(null);
          }}
          onClose={() => setPickerFor(null)}
          authed={authed} />
      )}
    </div>
  );
}

function ImageSlot({ data, onPick, onRemove, onFocal, onAlt, lang }) {
  const im = (data.images || [])[0] || {};
  const url = im.url;
  return (
    <div className="pt-3 border-t border-line/50 space-y-3">
      <div className="label-mono text-[11px] tracking-widest text-ink/60">IMAGE</div>
      {url ? (
        <>
          <div className="relative aspect-[16/9] bg-line/10 overflow-hidden">
            <img src={resolveImageUrl(url)} alt=""
                 className="w-full h-full object-cover"
                 style={{ objectPosition: `${im.focal?.x ?? 50}% ${im.focal?.y ?? 50}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FocalGrid label="Desktop focal" value={im.focal} onChange={f => onFocal("focal", f)} />
            <FocalGrid label="Mobile focal" value={im.focal_mobile} onChange={f => onFocal("focal_mobile", f)} />
          </div>
          <input value={(im.alt || {})[lang] || ""} onChange={e => onAlt(e.target.value)}
                 placeholder={`Alt text (${lang})`}
                 className="w-full border border-line px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={onPick}
                    className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Replace</button>
            <button onClick={onRemove}
                    className="border border-line text-ink/60 px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-rose-400 hover:text-rose-600">Remove</button>
          </div>
        </>
      ) : (
        <button onClick={onPick}
                data-testid="impact-pick-image"
                className="border-2 border-dashed border-line px-6 py-8 w-full text-ink/50 hover:border-ink hover:text-ink text-sm">
          + Select from Media Library
        </button>
      )}
    </div>
  );
}

function CauseEditor({ data, patch, lang, onPickLogo }) {
  const cause = data.cause || {};
  const set = (k, v) => patch("cause", { ...cause, [k]: v });
  const setLang = (k, v) => patch("cause", { ...cause, [k]: { ...(cause[k] || {}), [lang]: v } });
  return (
    <>
      <div className="pt-3 border-t border-line/50 space-y-3" data-testid="impact-cause-editor">
        <div className="label-mono text-[11px] tracking-widest text-ink/60">Cause identity</div>
        <input value={cause.name || ""} onChange={e => set("name", e.target.value)}
               placeholder="Cause name (e.g. RENACE Ávila)"
               className="border border-line px-3 py-2 w-full font-editorial text-lg" />
        <textarea value={(cause.description || {})[lang] || ""}
                  onChange={e => setLang("description", e.target.value)}
                  rows={2} placeholder={`Short description (${lang})`}
                  className="border border-line px-3 py-2 w-full text-sm" />
        <input value={cause.promoted_by || ""} onChange={e => set("promoted_by", e.target.value)}
               placeholder="Promoted by (organisations)"
               className="border border-line px-3 py-2 w-full text-sm" />
        <input value={cause.url || ""} onChange={e => set("url", e.target.value)}
               placeholder="Official website (leave blank until confirmed)"
               className="border border-line px-3 py-2 w-full text-sm font-mono" />
        <input value={(cause.cta_label || {})[lang] || ""}
               onChange={e => setLang("cta_label", e.target.value)}
               placeholder={`Button label (${lang})`} className="border border-line px-3 py-2 w-full text-sm" />
        <div className="flex items-center gap-4 text-[10px] text-ink/70">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={cause.name_link !== false}
                                                             onChange={e => set("name_link", e.target.checked)} /> Name clickable</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={cause.logo_link !== false}
                                                             onChange={e => set("logo_link", e.target.checked)} /> Logo clickable</label>
        </div>
        <div className="flex items-center gap-3">
          {cause.logo_url ? (
            <>
              <img src={resolveImageUrl(cause.logo_url)} alt={cause.name} className="max-h-12 border border-line p-1 bg-white" />
              <button onClick={() => set("logo_url", "")}
                      className="text-[10px] uppercase tracking-widest text-ink/60 hover:text-rose-600">Remove logo</button>
            </>
          ) : (
            <button onClick={onPickLogo}
                    className="text-[10px] uppercase tracking-widest border border-line px-2 py-1 hover:border-ink">+ Add logo</button>
          )}
        </div>
        <input value={cause.related_event_url || ""} onChange={e => set("related_event_url", e.target.value)}
               placeholder="Related event URL (e.g. /events/social-run-for-spain)"
               className="border border-line px-3 py-2 w-full text-sm font-mono" />
        <input value={(cause.related_event_label || {})[lang] || ""}
               onChange={e => setLang("related_event_label", e.target.value)}
               placeholder={`Related event label (${lang})`}
               className="border border-line px-3 py-2 w-full text-sm" />
      </div>

      <div className="pt-3 border-t border-line/50 space-y-3">
        <div className="label-mono text-[11px] tracking-widest text-ink/60">Contribution status</div>
        <textarea value={(data.status_label || {})[lang] || ""}
                  onChange={e => patch("status_label", { ...(data.status_label || {}), [lang]: e.target.value })}
                  rows={2}
                  placeholder={`Status text (${lang})`}
                  className="border border-line px-3 py-2 w-full text-sm italic" />
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink/70">
              <input type="checkbox" checked={!!data.show_final_amount}
                     onChange={e => patch("show_final_amount", e.target.checked)} /> Show final donation
            </label>
            <input value={data.final_amount || ""} onChange={e => patch("final_amount", e.target.value)}
                   placeholder="e.g. 50,000 DKK"
                   className="mt-2 border border-line px-3 py-2 w-full text-sm" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink/70">
              <input type="checkbox" checked={!!data.show_transfer_date}
                     onChange={e => patch("show_transfer_date", e.target.checked)} /> Show transfer date
            </label>
            <input value={data.transfer_date || ""} onChange={e => patch("transfer_date", e.target.value)}
                   placeholder="e.g. 15.11.2026"
                   className="mt-2 border border-line px-3 py-2 w-full text-sm" />
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

function FocalGrid({ label, value, onChange }) {
  const points = [
    { x: 0, y: 0 }, { x: 50, y: 0 }, { x: 100, y: 0 },
    { x: 0, y: 50 }, { x: 50, y: 50 }, { x: 100, y: 50 },
    { x: 0, y: 100 }, { x: 50, y: 100 }, { x: 100, y: 100 },
  ];
  const cur = value || { x: 50, y: 50 };
  return (
    <div>
      <div className="label-mono text-[10px] tracking-widest text-ink/60 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-1 w-fit border border-line p-1">
        {points.map(p => (
          <button key={`${p.x}-${p.y}`} onClick={() => onChange(p)}
                  className={`w-6 h-6 rounded-full ${cur.x === p.x && cur.y === p.y ? "bg-terracotta" : "bg-line/30 hover:bg-ink/40"}`} />
        ))}
      </div>
    </div>
  );
}

function PublishBatchModal({ modal, onConfirm, onClose }) {
  const { dry, result, busy, scope } = modal;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" data-testid="impact-publish-modal">
      <div className="bg-white max-w-lg w-full rounded-xl border border-line shadow-xl">
        <div className="p-6 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">IMPACT · BULK PUBLISH</div>
          <h3 className="font-editorial text-2xl tracking-tighter mt-1">
            {result ? "Done." : (scope === "selected" ? "Publish selected sections" : "Publish all sections")}
          </h3>
          <p className="text-xs text-ink/60 mt-2">
            {result ? "Global page state is unchanged. Public /impact still respects state."
                    : "This publishes section content only. Page state (DRAFT / READY / LIVE) is NOT changed here."}
          </p>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {!result && dry && (
            <>
              <Row label="Sections to publish" value={dry.to_publish} />
              <Row label="Already published (skipped)" value={dry.already_published} muted />
              <Row label="Hidden" value={dry.hidden} warn={dry.hidden > 0} />
              <Row label="Missing headline" value={dry.missing_headline} warn={dry.missing_headline > 0} />
              <Row label="Missing image (template requires one)" value={dry.missing_image} warn={dry.missing_image > 0} />
              <Row label="Missing link (button without URL)" value={dry.missing_link} warn={dry.missing_link > 0} />
              <div className="pt-2 border-t border-line/50">
                <div className="text-[11px] text-ink/60 mb-1">Translation gaps (not blocking)</div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniRow lang="EN" n={dry.missing_translation_by_lang.en} />
                  <MiniRow lang="DA" n={dry.missing_translation_by_lang.da} />
                  <MiniRow lang="ES" n={dry.missing_translation_by_lang.es} />
                </div>
              </div>
            </>
          )}
          {result && (
            <>
              <Row label="Sections published" value={result.published_count} />
              <Row label="Hidden after publish" value={result.hidden} muted />
              <Row label="Sections with missing image" value={result.missing_image} muted />
            </>
          )}
        </div>
        <div className="p-4 border-t border-line flex justify-end gap-2">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={onConfirm} disabled={busy || (dry?.to_publish || 0) === 0}
                      data-testid="impact-modal-confirm"
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest ${(dry?.to_publish || 0) === 0 ? "bg-line text-ink/40 cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                {busy ? "Publishing…" : `Confirm publish (${dry?.to_publish || 0})`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, warn }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-ink/50" : "text-ink/80"}>{label}</span>
      <span className={`font-editorial text-lg tabular-nums ${warn ? "text-rose-700" : muted ? "text-ink/40" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function MiniRow({ lang, n }) {
  return (
    <div className={`flex flex-col items-center py-2 rounded ${n > 0 ? "bg-amber-50 text-amber-800" : "bg-line/10 text-ink/40"}`}>
      <span className="font-editorial text-xl tabular-nums">{n}</span>
      <span className="text-[10px] label-mono tracking-widest">{lang}</span>
    </div>
  );
}
